<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';

import OutcomeBanner from '../../components/OutcomeBanner.vue';
import SecretOnce from '../../components/SecretOnce.vue';
import { adminOutcome, api, operationOutcome, type Outcome } from '../../api/client.js';
import { adminCommand, operation, operationBatch, revisionBasis, uuid } from '../../api/documents.js';
import {
  affectedPastureIds,
  existingVertexDraft,
  perimeterFromDrafts,
  vertexDraft,
  type PastureVertexDraft,
} from './pasture-editor.js';

/**
 * Área de operação de um cliente.
 *
 * Todas as escritas daqui usam os MESMOS documentos e as mesmas rotas do
 * aparelho. Não existe atalho: um botão que gravasse direto no banco criaria
 * dados que nenhuma regra conferiu.
 */
const props = defineProps<{ tenantId: string; tenantName: string; tenantCode: string }>();
const emit = defineEmits<{ catalogChanged: [] }>();

interface Farm { id: string; revision: string; name: string; status: string }
interface Pasture { id: string; revision: string; farmId: string; name: string; status: string; poleIds: string[] }
interface Pole {
  id: string;
  revision: string;
  status: string;
  coordinate: { latitude: number; longitude: number; origin: string; accuracyMeters: number | null } | null;
  farmIds: string[];
  tagId: string | null;
  bindingId: string | null;
}
interface Tag { id: string; epcRaw: string; epcNormalized: string; tagType: string; status: string; boundTo: string | null }
interface Animal { id: string; revision: string; label: string | null; active: boolean; farmId: string | null; expectedPastureId: string | null; tagId: string | null; bindingId: string | null }
interface Device { id: string; label: string; origin: string; status: string; credentialId: string | null; credentialPublicId: string | null }

const farms = ref<Farm[]>([]);
const pastures = ref<Pasture[]>([]);
const poles = ref<Pole[]>([]);
const tags = ref<Tag[]>([]);
const animals = ref<Animal[]>([]);
const devices = ref<Device[]>([]);
const revisions = ref<Record<string, string>>({});

const outcome = ref<Outcome | null>(null);
/** Segredo da última emissão/rotação; `packageJson` só para aparelho real. */
const delivery = ref<{ secret: string; packageJson: string | null } | null>(null);
const busy = ref(false);

const farmName = ref('');
const deviceLabel = ref('');
const deviceOrigin = ref<'' | 'real' | 'simulated'>('');
const tagEpc = ref('');
const tagType = ref<'cattle' | 'pole' | 'test'>('cattle');
const tagFormat = ref<'standard' | 'nonstandard'>('standard');
const selectedFarmId = ref<string>('');
const selectedAnimalId = ref<string>('');
const selectedTagId = ref<string>('');

const editingPastureId = ref<string | null>(null);
const pastureFarmId = ref('');
const pastureName = ref('');
const pastureVertices = ref<PastureVertexDraft[]>([]);
const formError = ref<string | null>(null);

const repositioningPoleId = ref<string | null>(null);
const poleLatitude = ref<number | null>(null);
const poleLongitude = ref<number | null>(null);
const replacementPoleTagId = ref('');
const replacementPoleReason = ref<'BROKEN' | 'LOST' | 'OTHER'>('BROKEN');

const availableTags = computed(() => tags.value.filter((tag) => tag.boundTo === null));
const availablePoleTags = computed(() =>
  tags.value.filter((tag) => tag.boundTo === null && tag.tagType === 'pole' && tag.status === 'active'),
);
const activePoles = computed(() => poles.value.filter((pole) => pole.status === 'active'));
const activeFarms = computed(() => farms.value.filter((farm) => farm.status === 'active'));

async function load(): Promise<void> {
  const scope = `/admin/tenants/${props.tenantId}`;
  const [farmsResult, pasturesResult, polesResult, tagsResult, animalsResult, devicesResult] = await Promise.all([
    api.list<{ items: Farm[] }>(`${scope}/farms?limit=100`),
    api.list<{ items: Pasture[] }>(`${scope}/pastures?limit=100`),
    api.list<{ items: Pole[] }>(`${scope}/poles?limit=100`),
    api.list<{ items: Tag[] }>(`${scope}/tags?limit=100`),
    api.list<{ items: Animal[] }>(`${scope}/animals?limit=100`),
    api.list<{ items: Device[] }>(`${scope}/devices?limit=100`),
  ]);
  farms.value = farmsResult.body.items ?? [];
  pastures.value = pasturesResult.body.items ?? [];
  poles.value = polesResult.body.items ?? [];
  tags.value = tagsResult.body.items ?? [];
  animals.value = animalsResult.body.items ?? [];
  devices.value = devicesResult.body.items ?? [];
  if (selectedFarmId.value === '' && activeFarms.value.length > 0) {
    selectedFarmId.value = activeFarms.value[0]!.id;
  }
  if (pastureFarmId.value === '' && activeFarms.value.length > 0) {
    pastureFarmId.value = activeFarms.value[0]!.id;
  }
}

/**
 * Revisão vigente de um agregado.
 *
 * A interface precisa declarar de qual estado partiu. Sem isso, duas telas
 * abertas ao mesmo tempo sobrescreveriam uma à outra sem que nada acusasse.
 */
async function revisionOf(kind: 'farm' | 'pasture' | 'animal' | 'pole', id: string): Promise<string> {
  const cached = revisions.value[`${kind}:${id}`];
  if (cached !== undefined) return cached;
  const response = await api.list<{ items: { id: string; revision?: string }[] }>(
    `/admin/tenants/${props.tenantId}/${kind}s?limit=100`,
  );
  const found = response.body.items.find((item) => item.id === id);
  return found?.revision ?? '1';
}

async function runCommand(
  kind: string,
  command: Record<string, unknown>,
): Promise<{ applied: boolean; secret: string | null }> {
  busy.value = true;
  try {
    const result = await api.command<Record<string, unknown>>(adminCommand(props.tenantId, kind, command));
    outcome.value = adminOutcome(result);
    const delivered = result.body.secret;
    const applied = outcome.value.kind === 'applied';
    if (applied) {
      await load();
      emit('catalogChanged');
    }
    // O segredo existe só nesta resposta; quem chamou guarda na tela, nunca em storage.
    return {
      applied,
      secret: applied && typeof delivered === 'string' && delivered !== '' ? delivered : null,
    };
  } finally {
    busy.value = false;
  }
}

async function runOperation(
  operationType: string,
  farmId: string | null,
  payload: Record<string, unknown>,
  preconditions: Record<string, unknown>[] = [],
): Promise<boolean> {
  busy.value = true;
  try {
    const envelope = await operationBatch(props.tenantId, [
      operation(props.tenantId, farmId, operationType, payload, { preconditions }),
    ]);
    const result = await api.operations<Record<string, unknown>>(envelope);
    const outcomes = operationOutcome(result);
    outcome.value = outcomes[0] ?? null;
    if (outcome.value?.kind === 'applied') {
      await load();
      return true;
    }
    return false;
  } finally {
    busy.value = false;
  }
}

const createFarm = () => runCommand('farm_create', { farmId: uuid(), name: farmName.value });

async function deactivateFarm(farm: Farm): Promise<void> {
  await runCommand('farm_deactivate', {
    farmId: farm.id,
    expectedRevision: await revisionOf('farm', farm.id),
  });
}

async function reactivateFarm(farm: Farm): Promise<void> {
  await runCommand('farm_reactivate', {
    farmId: farm.id,
    expectedRevision: await revisionOf('farm', farm.id),
  });
}

const createTag = () =>
  runCommand('tag_create', {
    tagId: uuid(),
    epcRaw: tagEpc.value,
    tagType: tagType.value,
    format: tagFormat.value,
  });

async function createDevice(): Promise<void> {
  if (busy.value) return;
  formError.value = null;
  const label = deviceLabel.value.trim();
  if (label === '' || deviceOrigin.value === '') {
    formError.value = 'Informe o nome e o tipo do dispositivo.';
    return;
  }
  // Identidade nova por instalação; nunca derivada de serial, MAC ou nome.
  const { applied } = await runCommand('device_create', {
    deviceInstallationId: uuid(),
    label,
    origin: deviceOrigin.value,
  });
  if (applied) {
    deviceLabel.value = '';
    deviceOrigin.value = '';
  }
}

/**
 * Emissão/rotação: o pacote do aparelho real é montado aqui, com o tenant e a
 * instalação da MESMA linha que recebeu a chave. Resposta sem segredo não
 * mostra nada — nem o pacote anterior.
 */
async function deliverCredential(device: Device, kind: string, command: Record<string, unknown>): Promise<void> {
  delivery.value = null;
  const tenantId = props.tenantId;
  const { secret } = await runCommand(kind, command);
  // Uma resposta atrasada do cliente anterior nunca pode recriar o segredo na tela atual.
  if (secret === null || props.tenantId !== tenantId) return;
  delivery.value = {
    secret,
    packageJson: device.origin === 'real'
      ? JSON.stringify({ tenantId, deviceInstallationId: device.id, deviceKey: secret })
      : null,
  };
}

const issueCredential = (device: Device) =>
  deliverCredential(device, 'device_credential_issue', {
    deviceInstallationId: device.id,
    credentialId: uuid(),
  });

const rotateCredential = (device: Device) =>
  deliverCredential(device, 'device_credential_rotate', {
    deviceInstallationId: device.id,
    credentialId: device.credentialId!,
    newCredentialId: uuid(),
    reason: 'rotação pela interface',
  });

async function enterAnimal(): Promise<void> {
  if (selectedFarmId.value === '') return;
  await runOperation('animal_entered', selectedFarmId.value, {
    kind: 'animal_entry',
    animalId: uuid(),
    label: null,
    farmId: selectedFarmId.value,
    entryReason: 'PURCHASE',
    expectedPastureId: null,
    binding:
      selectedTagId.value === ''
        ? null
        : {
            tagId: selectedTagId.value,
            bindingId: uuid(),
            tagReason: 'INITIAL',
            // Confirmação é dado do ato de cadastro, não leitura de campo.
            confirmation: { method: 'ADMIN_SELECTION', confirmedCode: null },
          },
  });
  selectedTagId.value = '';
}

async function replaceTag(): Promise<void> {
  const animal = animals.value.find((item) => item.id === selectedAnimalId.value);
  if (animal === undefined || animal.bindingId === null || selectedTagId.value === '') return;

  await runOperation(
    'animal_tag_replaced',
    animal.farmId,
    {
      kind: 'animal_tag_replacement',
      animalId: animal.id,
      oldTagId: animal.tagId,
      oldBindingId: animal.bindingId,
      newTagId: selectedTagId.value,
      newBindingId: uuid(),
      tagReason: 'BROKEN',
      confirmation: { method: 'ADMIN_SELECTION', confirmedCode: null },
    },
    [revisionBasis('animal', animal.id, await revisionOf('animal', animal.id))],
  );
  selectedTagId.value = '';
}

async function exitAnimal(animal: Animal): Promise<void> {
  await runOperation(
    'animal_exited',
    animal.farmId,
    {
      kind: 'animal_exit',
      animalId: animal.id,
      exitReason: 'SALE',
      expectedBindingId: animal.bindingId,
    },
    [revisionBasis('animal', animal.id, await revisionOf('animal', animal.id))],
  );
}

async function transferAnimal(animal: Animal, toFarmId: string): Promise<void> {
  if (toFarmId === '') return;
  await runOperation(
    'animal_farm_transferred',
    animal.farmId,
    {
      kind: 'animal_farm_transfer',
      animalId: animal.id,
      fromFarmId: animal.farmId,
      toFarmId,
      destinationPastureId: null,
    },
    [revisionBasis('animal', animal.id, await revisionOf('animal', animal.id))],
  );
}

function resetPastureForm(): void {
  editingPastureId.value = null;
  pastureName.value = '';
  pastureFarmId.value = activeFarms.value[0]?.id ?? '';
  pastureVertices.value = [vertexDraft(uuid), vertexDraft(uuid), vertexDraft(uuid)];
  formError.value = null;
}

function editPasture(pasture: Pasture): void {
  editingPastureId.value = pasture.id;
  pastureName.value = pasture.name;
  pastureFarmId.value = pasture.farmId;
  pastureVertices.value = pasture.poleIds.map((poleId) => existingVertexDraft(uuid, poleId));
  formError.value = null;
}

function addPastureVertex(): void {
  pastureVertices.value.push(vertexDraft(uuid));
}

function removePastureVertex(index: number): void {
  pastureVertices.value.splice(index, 1);
}

function movePastureVertex(index: number, delta: -1 | 1): void {
  const destination = index + delta;
  if (destination < 0 || destination >= pastureVertices.value.length) return;
  const [vertex] = pastureVertices.value.splice(index, 1);
  if (vertex !== undefined) pastureVertices.value.splice(destination, 0, vertex);
}

function changeVertexMode(vertex: PastureVertexDraft): void {
  if (vertex.mode === 'new') {
    vertex.poleId = uuid();
    vertex.bindingId = uuid();
    vertex.tagId = '';
    vertex.latitude = null;
    vertex.longitude = null;
  } else {
    vertex.poleId = '';
    vertex.tagId = '';
  }
}

async function savePasture(): Promise<void> {
  formError.value = null;
  try {
    if (pastureFarmId.value === '') throw new Error('Selecione a fazenda.');
    if (pastureName.value.trim() === '') throw new Error('Informe o nome do pasto.');
    const perimeter = perimeterFromDrafts(pastureVertices.value);
    const current = pastures.value.find((pasture) => pasture.id === editingPastureId.value);
    const pastureId = current?.id ?? uuid();
    const applied = await runOperation(
      current === undefined ? 'pasture_created' : 'pasture_edited',
      pastureFarmId.value,
      {
        kind: current === undefined ? 'pasture_created' : 'pasture_edited',
        pastureId,
        farmId: pastureFarmId.value,
        name: pastureName.value.trim(),
        perimeter,
      },
      current === undefined ? [] : [revisionBasis('pasture', current.id, current.revision)],
    );
    if (applied) {
      resetPastureForm();
      emit('catalogChanged');
    }
  } catch (error) {
    formError.value = error instanceof Error ? error.message : 'Não foi possível montar o pasto.';
  }
}

function editPole(pole: Pole): void {
  repositioningPoleId.value = pole.id;
  poleLatitude.value = pole.coordinate?.latitude ?? null;
  poleLongitude.value = pole.coordinate?.longitude ?? null;
  replacementPoleTagId.value = '';
  formError.value = null;
}

function cancelPoleEdit(): void {
  repositioningPoleId.value = null;
  poleLatitude.value = null;
  poleLongitude.value = null;
  replacementPoleTagId.value = '';
}

async function replacePoleTag(): Promise<void> {
  formError.value = null;
  try {
    const pole = poles.value.find((item) => item.id === repositioningPoleId.value);
    if (pole === undefined || pole.tagId === null || pole.bindingId === null) {
      throw new Error('O poste não possui um vínculo de etiqueta vigente.');
    }
    if (replacementPoleTagId.value === '') throw new Error('Selecione a nova etiqueta.');
    const applied = await runOperation(
      'pole_tag_replaced',
      null,
      {
        kind: 'pole_tag_replacement',
        poleId: pole.id,
        oldTagId: pole.tagId,
        oldBindingId: pole.bindingId,
        newTagId: replacementPoleTagId.value,
        newBindingId: uuid(),
        tagReason: replacementPoleReason.value,
        confirmation: { method: 'ADMIN_SELECTION', confirmedCode: null },
      },
      [revisionBasis('pole', pole.id, pole.revision)],
    );
    if (applied) {
      cancelPoleEdit();
      emit('catalogChanged');
    }
  } catch (error) {
    formError.value = error instanceof Error ? error.message : 'Não foi possível trocar a etiqueta do poste.';
  }
}

async function savePolePosition(): Promise<void> {
  formError.value = null;
  try {
    const pole = poles.value.find((item) => item.id === repositioningPoleId.value);
    if (pole === undefined) throw new Error('Selecione o poste.');
    if (
      poleLatitude.value === null || poleLongitude.value === null ||
      !Number.isFinite(poleLatitude.value) || !Number.isFinite(poleLongitude.value) ||
      poleLatitude.value < -90 || poleLatitude.value > 90 ||
      poleLongitude.value < -180 || poleLongitude.value > 180
    ) {
      throw new Error('Informe latitude entre -90 e 90 e longitude entre -180 e 180.');
    }
    const applied = await runOperation(
      'pole_repositioned',
      null,
      {
        kind: 'pole_repositioned',
        poleId: pole.id,
        coordinate: {
          latitude: poleLatitude.value,
          longitude: poleLongitude.value,
          origin: 'MANUAL_MAP',
          accuracyMeters: null,
        },
        affectedPastureIds: affectedPastureIds(pastures.value, pole.id),
      },
      [revisionBasis('pole', pole.id, pole.revision)],
    );
    if (applied) {
      cancelPoleEdit();
      emit('catalogChanged');
    }
  } catch (error) {
    formError.value = error instanceof Error ? error.message : 'Não foi possível reposicionar o poste.';
  }
}

onMounted(async () => {
  await load();
  resetPastureForm();
});
// Segredo não sobrevive à troca de cliente nem à saída da tela.
watch(() => props.tenantId, () => { delivery.value = null; });
onUnmounted(() => { delivery.value = null; });
defineExpose({ load, runCommand, runOperation });
</script>

<template>
  <section class="workspace">
    <header>
      <h2>{{ tenantName }} <code>{{ tenantCode }}</code></h2>
    </header>

    <OutcomeBanner :outcome="outcome" />
    <p
      v-if="formError !== null"
      class="form-error"
      role="alert"
    >
      {{ formError }}
    </p>

    <section>
      <h3>Fazendas</h3>
      <form
        class="row"
        @submit.prevent="createFarm"
      >
        <label>Nome <input
          v-model="farmName"
          name="farm-name"
          required
        ></label>
        <button
          type="submit"
          :disabled="busy"
        >
          Criar fazenda
        </button>
      </form>
      <ul>
        <li
          v-for="farm in farms"
          :key="farm.id"
          :data-farm="farm.id"
        >
          {{ farm.name }} — {{ farm.status }}
          <button
            v-if="farm.status === 'active'"
            type="button"
            :disabled="busy"
            @click="deactivateFarm(farm)"
          >
            Desativar
          </button>
          <button
            v-else
            type="button"
            :disabled="busy"
            @click="reactivateFarm(farm)"
          >
            Reativar
          </button>
        </li>
      </ul>
    </section>

    <section>
      <h3>Etiquetas</h3>
      <form
        class="row"
        @submit.prevent="createTag"
      >
        <label>EPC <input
          v-model="tagEpc"
          name="tag-epc"
          required
        ></label>
        <label>
          Tipo
          <select
            v-model="tagType"
            name="tag-type"
          >
            <option value="cattle">gado</option>
            <option value="pole">poste</option>
            <option value="test">teste</option>
          </select>
        </label>
        <label>
          Formato
          <select
            v-model="tagFormat"
            name="tag-format"
          >
            <option value="standard">padrão</option>
            <option value="nonstandard">fora do padrão</option>
          </select>
        </label>
        <button
          type="submit"
          :disabled="busy"
        >
          Cadastrar etiqueta
        </button>
      </form>
      <ul>
        <li
          v-for="tag in tags"
          :key="tag.id"
          :data-tag="tag.id"
        >
          <code>{{ tag.epcNormalized }}</code> — {{ tag.tagType }} —
          {{ tag.boundTo === null ? 'disponível' : 'vinculada' }}
        </li>
      </ul>
    </section>

    <section>
      <h3>Animais</h3>
      <form
        class="row"
        @submit.prevent="enterAnimal"
      >
        <label>
          Fazenda
          <select
            v-model="selectedFarmId"
            name="animal-farm"
          >
            <option
              v-for="farm in activeFarms"
              :key="farm.id"
              :value="farm.id"
            >{{ farm.name }}</option>
          </select>
        </label>
        <label>
          Etiqueta
          <select
            v-model="selectedTagId"
            name="animal-tag"
          >
            <option value="">sem etiqueta</option>
            <option
              v-for="tag in availableTags"
              :key="tag.id"
              :value="tag.id"
            >{{ tag.epcNormalized }}</option>
          </select>
        </label>
        <button
          type="submit"
          :disabled="busy"
        >
          Registrar entrada
        </button>
      </form>

      <form
        class="row"
        @submit.prevent="replaceTag"
      >
        <label>
          Animal
          <select
            v-model="selectedAnimalId"
            name="replace-animal"
          >
            <option
              v-for="animal in animals.filter((item) => item.active && item.bindingId)"
              :key="animal.id"
              :value="animal.id"
            >
              {{ animal.label ?? animal.id.slice(0, 8) }}
            </option>
          </select>
        </label>
        <label>
          Nova etiqueta
          <select
            v-model="selectedTagId"
            name="replace-tag"
          >
            <option value="">selecione</option>
            <option
              v-for="tag in availableTags"
              :key="tag.id"
              :value="tag.id"
            >{{ tag.epcNormalized }}</option>
          </select>
        </label>
        <button
          type="submit"
          :disabled="busy"
        >
          Trocar etiqueta
        </button>
      </form>

      <table>
        <thead><tr><th>Animal</th><th>Fazenda</th><th>Etiqueta</th><th>Situação</th><th /></tr></thead>
        <tbody>
          <tr
            v-for="animal in animals"
            :key="animal.id"
            :data-animal="animal.id"
          >
            <td>{{ animal.label ?? animal.id.slice(0, 8) }}</td>
            <td>{{ farms.find((farm) => farm.id === animal.farmId)?.name ?? '—' }}</td>
            <td>{{ tags.find((tag) => tag.id === animal.tagId)?.epcNormalized ?? '—' }}</td>
            <td>{{ animal.active ? 'ativo' : 'baixado' }}</td>
            <td>
              <button
                v-if="animal.active"
                type="button"
                :disabled="busy"
                @click="exitAnimal(animal)"
              >
                Baixa
              </button>
              <select
                v-if="animal.active"
                :name="`transfer-${animal.id}`"
                @change="transferAnimal(animal, ($event.target as HTMLSelectElement).value)"
              >
                <option value="">
                  transferir…
                </option>
                <option
                  v-for="farm in activeFarms.filter((item) => item.id !== animal.farmId)"
                  :key="farm.id"
                  :value="farm.id"
                >
                  {{ farm.name }}
                </option>
              </select>
            </td>
          </tr>
        </tbody>
      </table>
    </section>

    <section>
      <h3>Pastos</h3>
      <p>
        O perímetro segue a ordem abaixo. Use as setas para inverter postes sem
        apagar o pasto. Postes novos exigem uma etiqueta de poste disponível.
      </p>
      <form
        class="pasture-form"
        data-testid="pasture-form"
        @submit.prevent="savePasture"
      >
        <div class="row">
          <label>
            Fazenda
            <select
              v-model="pastureFarmId"
              name="pasture-farm"
              required
              :disabled="editingPastureId !== null"
            >
              <option
                v-for="farm in activeFarms"
                :key="farm.id"
                :value="farm.id"
              >{{ farm.name }}</option>
            </select>
          </label>
          <label>Nome <input
            v-model="pastureName"
            name="pasture-name"
            required
          ></label>
        </div>

        <ol class="vertices">
          <li
            v-for="(vertex, index) in pastureVertices"
            :key="vertex.key"
            class="vertex"
          >
            <strong>Poste {{ index + 1 }}</strong>
            <label>
              Origem
              <select
                v-model="vertex.mode"
                :name="`vertex-mode-${index}`"
                @change="changeVertexMode(vertex)"
              >
                <option value="existing">poste existente</option>
                <option value="new">novo poste</option>
              </select>
            </label>
            <label v-if="vertex.mode === 'existing'">
              Poste
              <select
                v-model="vertex.poleId"
                :name="`vertex-pole-${index}`"
                required
              >
                <option value="">selecione</option>
                <option
                  v-for="pole in activePoles"
                  :key="pole.id"
                  :value="pole.id"
                >
                  {{ tags.find((tag) => tag.id === pole.tagId)?.epcNormalized ?? pole.id.slice(0, 8) }}
                </option>
              </select>
            </label>
            <template v-else>
              <label>
                Etiqueta
                <select
                  v-model="vertex.tagId"
                  :name="`vertex-tag-${index}`"
                  required
                >
                  <option value="">selecione</option>
                  <option
                    v-for="tag in availablePoleTags"
                    :key="tag.id"
                    :value="tag.id"
                  >{{ tag.epcNormalized }}</option>
                </select>
              </label>
              <label>Latitude <input
                v-model.number="vertex.latitude"
                :name="`vertex-latitude-${index}`"
                type="number"
                min="-90"
                max="90"
                step="any"
                required
              ></label>
              <label>Longitude <input
                v-model.number="vertex.longitude"
                :name="`vertex-longitude-${index}`"
                type="number"
                min="-180"
                max="180"
                step="any"
                required
              ></label>
            </template>
            <div class="vertex-actions">
              <button
                type="button"
                aria-label="Mover poste para cima"
                :disabled="index === 0 || busy"
                @click="movePastureVertex(index, -1)"
              >
                ↑
              </button>
              <button
                type="button"
                aria-label="Mover poste para baixo"
                :disabled="index === pastureVertices.length - 1 || busy"
                @click="movePastureVertex(index, 1)"
              >
                ↓
              </button>
              <button
                type="button"
                :disabled="busy"
                @click="removePastureVertex(index)"
              >
                Remover
              </button>
            </div>
          </li>
        </ol>
        <div class="row">
          <button
            type="button"
            :disabled="busy"
            @click="addPastureVertex"
          >
            Adicionar poste
          </button>
          <button
            type="submit"
            :disabled="busy"
          >
            {{ editingPastureId === null ? 'Criar pasto' : 'Salvar pasto' }}
          </button>
          <button
            v-if="editingPastureId !== null"
            type="button"
            :disabled="busy"
            @click="resetPastureForm"
          >
            Cancelar edição
          </button>
        </div>
      </form>

      <ul>
        <li
          v-for="pasture in pastures"
          :key="pasture.id"
          :data-pasture="pasture.id"
        >
          {{ pasture.name }} — {{ pasture.status }} — {{ pasture.poleIds.length }} postes
          <button
            v-if="pasture.status === 'active'"
            type="button"
            :disabled="busy"
            @click="editPasture(pasture)"
          >
            Editar
          </button>
        </li>
      </ul>
      <p v-if="pastures.length === 0">
        Nenhum pasto cadastrado.
      </p>
    </section>

    <section>
      <h3>Postes</h3>
      <p>
        Um poste é criado como vértice de um pasto. Aqui é possível ajustar sua
        posição; todos os pastos ativos que o utilizam são atualizados juntos.
      </p>
      <form
        v-if="repositioningPoleId !== null"
        class="row"
        data-testid="pole-position-form"
        @submit.prevent="savePolePosition"
      >
        <label>Latitude <input
          v-model.number="poleLatitude"
          name="pole-latitude"
          type="number"
          min="-90"
          max="90"
          step="any"
          required
        ></label>
        <label>Longitude <input
          v-model.number="poleLongitude"
          name="pole-longitude"
          type="number"
          min="-180"
          max="180"
          step="any"
          required
        ></label>
        <button
          type="submit"
          :disabled="busy"
        >
          Salvar posição
        </button>
        <button
          type="button"
          :disabled="busy"
          @click="cancelPoleEdit"
        >
          Cancelar
        </button>
      </form>
      <form
        v-if="repositioningPoleId !== null"
        class="row"
        data-testid="pole-tag-form"
        @submit.prevent="replacePoleTag"
      >
        <label>
          Nova etiqueta
          <select
            v-model="replacementPoleTagId"
            name="replacement-pole-tag"
            required
          >
            <option value="">selecione</option>
            <option
              v-for="tag in availablePoleTags"
              :key="tag.id"
              :value="tag.id"
            >{{ tag.epcNormalized }}</option>
          </select>
        </label>
        <label>
          Motivo
          <select
            v-model="replacementPoleReason"
            name="replacement-pole-reason"
          >
            <option value="BROKEN">etiqueta quebrada</option>
            <option value="LOST">etiqueta perdida</option>
            <option value="OTHER">outro</option>
          </select>
        </label>
        <button
          type="submit"
          :disabled="busy"
        >
          Trocar etiqueta
        </button>
      </form>
      <table>
        <thead><tr><th>Etiqueta</th><th>Coordenada</th><th>Pastos</th><th /></tr></thead>
        <tbody>
          <tr
            v-for="pole in activePoles"
            :key="pole.id"
            :data-pole="pole.id"
          >
            <td><code>{{ tags.find((tag) => tag.id === pole.tagId)?.epcNormalized ?? '—' }}</code></td>
            <td>
              {{ pole.coordinate === null ? 'sem posição' : `${pole.coordinate.latitude}, ${pole.coordinate.longitude}` }}
            </td>
            <td>
              {{ affectedPastureIds(pastures, pole.id).length }}
            </td>
            <td>
              <button
                type="button"
                :disabled="busy"
                @click="editPole(pole)"
              >
                Editar posição
              </button>
            </td>
          </tr>
        </tbody>
      </table>
      <p v-if="activePoles.length === 0">
        Nenhum poste cadastrado.
      </p>
    </section>

    <section data-testid="devices-section">
      <h3>Dispositivos</h3>
      <SecretOnce
        :secret="delivery?.secret ?? null"
        :package-json="delivery?.packageJson ?? null"
        @dismiss="delivery = null"
      />
      <form
        class="row"
        data-testid="device-form"
        @submit.prevent="createDevice"
      >
        <label>Nome <input
          v-model="deviceLabel"
          name="device-label"
          maxlength="200"
          required
        ></label>
        <label>
          Tipo
          <select
            v-model="deviceOrigin"
            name="device-origin"
            required
          >
            <option value="">selecione</option>
            <option value="real">Real — TC58/coletor de campo</option>
            <option value="simulated">Simulado — executor automático</option>
          </select>
        </label>
        <button
          type="submit"
          :disabled="busy"
        >
          Cadastrar dispositivo
        </button>
      </form>
      <ul>
        <li
          v-for="device in devices"
          :key="device.id"
          :data-device="device.id"
        >
          {{ device.label }} — {{ device.origin === 'real' ? 'Real' : 'Simulado' }} — {{ device.status }} —
          ID da instalação: <code class="copyable">{{ device.id }}</code> —
          ID público: <code>{{ device.credentialPublicId ?? 'sem chave' }}</code>
          <button
            v-if="device.credentialId === null"
            type="button"
            :disabled="busy"
            @click="issueCredential(device)"
          >
            Emitir chave
          </button>
          <button
            v-else
            type="button"
            :disabled="busy"
            @click="rotateCredential(device)"
          >
            Rotacionar chave
          </button>
        </li>
      </ul>
    </section>
  </section>
</template>

<style scoped>
.workspace { display: grid; gap: 1.5rem; }
.row { display: flex; gap: .75rem; align-items: end; flex-wrap: wrap; margin-bottom: .5rem; }
.pasture-form { border: 1px solid #ddd; border-radius: .5rem; padding: 1rem; }
.vertices { display: grid; gap: .75rem; padding-left: 1.5rem; }
.vertex { border-bottom: 1px solid #eee; display: flex; gap: .75rem; align-items: end; flex-wrap: wrap; padding-bottom: .75rem; }
.vertex-actions { display: flex; gap: .25rem; }
.form-error { background: #fff0f0; border: 1px solid #c33; color: #7a1111; padding: .75rem; }
table { width: 100%; border-collapse: collapse; }
th, td { border-bottom: 1px solid #ddd; text-align: left; padding: .4rem; }
.copyable { user-select: all; }
</style>
